import React from 'react';
import update from 'immutability-helper';
import { uniqueId, flattenDeep } from 'lodash/fp';
import ModalContainer from 'components/shared/modal/modal_container';
import { Icon } from 'ui';
import { withTooltip } from 'components/shared/Tooltip';
import request from 'utils/request_ajax';
import { routes } from 'constants/config';
import addModifiers from 'utils/add_modifiers';
import RuleSet from './RuleSet';
import RuleBuilderModalLoadingState from './RuleBuilderModalLoadingState';
import {
  focusLastFocusableNode,
  focusFirstFocusableNode,
} from '../../../ui/utilities/focus';

class RuleBuilderModalContainer extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      name: null,
      submitEnabled: false,
      ruleSets: [this.ruleSetObject()],
      loading: true,
      nameError: null,
      networkError: null,
      errorTooltip: null,
      hasActiveTriggers: false,
    };
    this.fetchData = this.fetchData.bind(this);
    this.validateGroup = this.validateGroup.bind(this);
    this.onTitleChange = this.onTitleChange.bind(this);
    this.addNewRule = this.addNewRule.bind(this);
    this.updateRule = this.updateRule.bind(this);
    this.ruleSetObject = this.ruleSetObject.bind(this);
    this.addNewRuleSet = this.addNewRuleSet.bind(this);
    this.saveRuleBasedGroup = this.saveRuleBasedGroup.bind(this);
    this.removeRule = this.removeRule.bind(this);
  }

  modalAccessRef = React.createRef();

  fetchData() {
    const route = routes.SMART_GROUPS + '/' + (this.props.activeGroup || '');
    request.get(route).then(payload => {
      if (this.props.editing) {
        this.setState(
          {
            name: payload.data.name,
            id: payload.data.id,
            ruleSets: payload.data.ruleSets,
            loading: false,
            builderRules: payload.meta,
            hasActiveTriggers: payload.data.hasActiveTriggers,
          },
          this.validateGroup,
        );
      } else {
        this.setState(
          { loading: false, builderRules: payload.meta },
          this.validateGroup,
        );
      }
    });
  }

  validateGroup() {
    const { editing } = this.props;
    const hasName = !!this.state.name && this.state.name.trim().length > 0;
    const rulesArray = flattenDeep(
      this.state.ruleSets.map(ruleSet => ruleSet.rules.map(rule => rule)),
    );
    const validRules = rulesArray.filter(rule => rule.valid === true);
    const hasInvalidRules = validRules.length !== rulesArray.length;

    let errorTooltip;
    if (!hasName || (!hasName && !editing)) {
      errorTooltip = t('smart_groups.create_modal.add_name_tooltip');
    } else if (hasName && !editing && validRules.length === 0) {
      errorTooltip = t('smart_groups.create_modal.no_rules_tooltip');
    } else if (hasInvalidRules) {
      errorTooltip = t('smart_groups.create_modal.invalid_rules_tooltip');
    }

    this.setState({ errorTooltip, submitEnabled: hasName && !hasInvalidRules });
  }

  onTitleChange(e) {
    this.setState(
      { name: e.target.value, nameError: null },
      this.validateGroup,
    );
  }

  static ruleObject() {
    return { name: null, key: uniqueId('rule') };
  }

  addNewRule(ruleSetIndex) {
    this.setState(
      state => ({
        ruleSets: update(state.ruleSets, {
          [ruleSetIndex]: {
            rules: {
              $push: [this.constructor.ruleObject()],
            },
          },
        }),
      }),
      this.validateGroup,
    );
  }

  updateRule(ruleIndex, ruleSetIndex, data) {
    this.setState(
      state => ({
        ruleSets: update(state.ruleSets, {
          [ruleSetIndex]: {
            rules: {
              [ruleIndex]: {
                $merge: data,
              },
            },
          },
        }),
      }),
      this.validateGroup,
    );
  }

  removeRule(ruleIndex, ruleSetIndex) {
    const { ruleSets } = this.state;
    const ruleSet = this.state.ruleSets[ruleSetIndex];

    if (ruleSet.rules.length > 1) {
      this.setState(
        {
          ruleSets: update(ruleSets, {
            [ruleSetIndex]: {
              rules: {
                $set: [
                  ...ruleSet.rules.slice(0, ruleIndex),
                  ...ruleSet.rules.slice(ruleIndex + 1),
                ],
              },
            },
          }),
        },
        this.validateGroup,
      );
    } else if (ruleSets.length > 1) {
      this.setState(
        {
          ruleSets: [
            ...ruleSets.slice(0, ruleSetIndex),
            ...ruleSets.slice(ruleSetIndex + 1),
          ],
        },
        this.validateGroup,
      );
    }
  }

  ruleSetObject() {
    return {
      key: uniqueId('ruleSet'),
      rules: [this.constructor.ruleObject()],
    };
  }

  addNewRuleSet() {
    this.setState(
      state => ({
        ruleSets: [...state.ruleSets, this.ruleSetObject()],
      }),
      this.validateGroup,
    );
  }

  saveRuleBasedGroup() {
    const ruleData = {
      name: this.state.name,
      ruleSets: this.state.ruleSets,
    };

    this.setState({ submitEnabled: false });

    if (this.props.editing) {
      request
        .putJSON(routes.SMART_GROUPS + '/' + this.props.activeGroup, ruleData)
        .then(data => {
          if (data.success) {
            this.props.setGroupInMeta({
              name: this.state.name,
              id: this.state.id,
              recalculationState: null,
            });
            this.props.updateActiveGroup(this.state.id);
            this.props.closeModal();
          } else if (data.errors.group.startsWith('Name')) {
            this.setState({ nameError: data.errors.group });
          } else {
            // eslint-disable-next-line no-alert
            alert(data.errors.group);
          }
        })
        .fail(data => {
          this.setState({
            networkError: t('smart_groups.create_modal.save_error'),
          });
        });
    } else {
      request
        .postJSON(routes.SMART_GROUPS, ruleData)
        .then(data => {
          if (data.success) {
            this.props.setGroupInMeta({
              name: this.state.name,
              type: 'Group::Smart',
              id: data.id,
            });
            this.props.updateActiveGroup(data.id);
            this.props.closeModal();
          } else if (data.errors.group.startsWith('Name')) {
            this.setState({ nameError: data.errors.group });
          } else {
            // eslint-disable-next-line no-alert
            alert(data.errors.group);
          }
        })
        .fail(data => {
          this.setState({
            networkError: t('smart_groups.create_modal.create_error'),
          });
        });
    }
  }

  handleFocusWrap = first => () => {
    if (this.modalAccessRef.current) {
      if (first) {
        focusLastFocusableNode(this.modalAccessRef.current);
      } else {
        focusFirstFocusableNode(this.modalAccessRef.current);
      }
    }
  };

  componentDidMount() {
    this.fetchData();
    setTimeout(() => {
      if (this.modalAccessRef.current && !this.props.manualAutoFocus) {
        focusFirstFocusableNode(this.modalAccessRef.current);
      }
    }, 0);
  }

  render() {
    const { closeModal, editing, customGroups } = this.props;
    const {
      name,
      networkError,
      nameError,
      loading,
      ruleSets,
      submitEnabled,
      errorTooltip,
      hasActiveTriggers,
    } = this.state;

    return (
      <ModalContainer
        className="ModalTakeover-background Modal-background"
        onEscape={closeModal}
      >
        <div
          role="button"
          tabIndex={0}
          aria-hidden="true"
          onFocus={this.handleFocusWrap(true)}
        />
        <div ref={this.modalAccessRef} className="ModalTakeover-container">
          <div
            className="ModalTakeover-closeButton"
            onClick={closeModal}
            role="button"
            tabIndex="0"
            onKeyDown={e => e.key === 'Enter' && closeModal()}
          >
            <Icon name="error" />
          </div>
          {loading && <RuleBuilderModalLoadingState editing={editing} />}
          {!loading && (
            <div>
              <div className="ModalTakeover-header">
                <div className="ModalTakeover-title">
                  {editing
                    ? t('smart_groups.create_modal.edit_title')
                    : t('smart_groups.create_modal.new_title')}
                </div>
                <div className="mb-8">{t('groups.group_name')}</div>
                <input
                  className="NewInput NewInput--white NewInput--fw ModalTakeover-nameInput"
                  onChange={this.onTitleChange}
                  placeholder={t('smart_groups.create_modal.name_placeholder')}
                  type="text"
                  value={name || ''}
                />
                {nameError && (
                  <span className="ErrorContainer ErrorContainer--brandRed Animated--fadeIn d-ib mt-8 mb-0">
                    <Icon name="warning_sign" className="mr-8" />
                    {nameError}
                  </span>
                )}
              </div>
              <div className="ModalTakeover-main">
                <div className="fz-bigger mb-32 c-brand-gray-darker">
                  {t('smart_groups.create_modal.instructions')}
                </div>
                {ruleSets.map((ruleSet, ruleSetIndex) => (
                  <RuleSet
                    addNewRule={this.addNewRule}
                    addNewRuleSet={this.addNewRuleSet}
                    customGroups={customGroups}
                    editing={editing}
                    ruleSetIndex={ruleSetIndex}
                    key={editing ? ruleSetIndex : ruleSet.key}
                    last={ruleSetIndex === ruleSets.length - 1}
                    ruleSet={ruleSet}
                    removeRule={this.removeRule}
                    updateRule={this.updateRule}
                    builderRules={this.state.builderRules}
                  />
                ))}
                {hasActiveTriggers && (
                  <div className="d-f jc-sb mb-12 mt-12 c-red">
                    <Icon name="warning_sign" className="mr-16 fz-h2" />
                    <div>{t('smart_groups.create_modal.trigger_warning')}</div>
                  </div>
                )}
                <div className="ModalTakeover-buttonContainer d-f ai-fe jc-sb">
                  {networkError && (
                    <span className="ErrorContainer ErrorContainer--brandRed Animated--fadeIn d-ib mt-8 mb-0">
                      <Icon name="warning_sign" className="mr-8" />
                      {networkError}
                    </span>
                  )}
                  <div className="ModalTakeover-buttons ml-a">
                    {withTooltip({
                      text: errorTooltip,
                      inline: true,
                    })(
                      <button
                        type="button"
                        onClick={closeModal}
                        className={addModifiers('NewButton', [
                          'tallAndWide',
                          'flat',
                        ])}
                      >
                        {t('common.cancel')}
                      </button>,
                    )}
                    {withTooltip({
                      text: errorTooltip,
                      inline: true,
                    })(
                      <button
                        type="button"
                        onClick={this.saveRuleBasedGroup}
                        className="NewButton NewButton--tallAndWide CreateGroup-button"
                        disabled={!submitEnabled || nameError}
                      >
                        {editing
                          ? t('groups.save_group')
                          : t('groups.create_group')}
                      </button>,
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
        <div
          role="button"
          tabIndex={0}
          aria-hidden="true"
          onFocus={this.handleFocusWrap(false)}
        />
      </ModalContainer>
    );
  }
}

export default RuleBuilderModalContainer;
